<template>
    <div class="s-py-100">
        <div class="container">
            <div class="max-w-[1220px] mx-auto">
                <div v-if="validationErrors.length" class="mb-6 p-4 border border-red-300 text-red-700 rounded">
                    <ul class="list-disc pl-6">
                        <li v-for="(error, index) in validationErrors" :key="`error-${index}`">{{ error }}</li>
                    </ul>
                </div>

                <div v-if="flashMessage.success" class="mb-6 p-4 border border-green-300 text-green-700 rounded">
                    {{ flashMessage.success }}
                </div>
                <div v-if="flashMessage.warning" class="mb-6 p-4 border border-yellow-300 text-yellow-800 rounded">
                    {{ flashMessage.warning }}
                </div>
                <div v-if="flashMessage.error" class="mb-6 p-4 border border-red-300 text-red-700 rounded">
                    {{ flashMessage.error }}
                </div>

                <div
                    v-if="showPendingOrdersBox && pendingOrders.length"
                    class="mb-6 p-4 border border-yellow-300 bg-yellow-50 text-yellow-900 rounded"
                >
                    <div class="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <div class="font-semibold">Tienes pedidos pendientes o en curso.</div>
                            <div class="text-sm">¿Deseas retomar uno de esos pedidos antes de crear una orden nueva?</div>
                        </div>
                        <div class="flex flex-wrap gap-2">
                            <a
                                v-if="latestPendingOrder && latestPendingOrder.resume_url"
                                class="px-4 py-2 rounded bg-primary text-white text-sm"
                                :href="latestPendingOrder.resume_url"
                            >
                                Retomar último pedido
                            </a>
                            <button
                                type="button"
                                class="px-4 py-2 rounded border border-yellow-500 text-yellow-900 text-sm"
                                @click="dismissPendingOrders"
                            >
                                Continuar compra nueva
                            </button>
                        </div>
                    </div>

                    <div class="mt-3 grid gap-2">
                        <a
                            v-for="order in pendingOrders"
                            :key="`pending-${order.id}`"
                            class="block p-3 rounded border border-yellow-200 bg-white hover:bg-yellow-100 duration-200"
                            :href="order.resume_url"
                        >
                            <div class="flex flex-wrap items-center justify-between gap-2">
                                <div class="text-sm">
                                    <span class="font-semibold">{{ order.code }}</span>
                                    · {{ formatDateTime(order.created_at) }}
                                    · {{ formatMoney(order.total) }} {{ order.currency }}
                                </div>
                                <div class="text-xs text-yellow-800">
                                    Orden: {{ mapOrderStatus(order.status) }}
                                    · Pago: {{ mapPaymentStatus(order.payment_status) }}
                                    <template v-if="Number(order.open_payment_events_count || 0) > 0">
                                        · Transacciones en curso: {{ Number(order.open_payment_events_count || 0) }}
                                    </template>
                                </div>
                            </div>
                        </a>
                    </div>
                </div>
            </div>

            <div class="max-w-[1220px] mx-auto grid lg:grid-cols-2 gap-[30px] lg:gap-[70px]">
                <div class="bg-[#FAFAFA] dark:bg-dark-secondary p-[30px] md:p-[40px] lg:p-[50px] border border-[#17243026] border-opacity-15 rounded-xl">
                    <h4 class="font-semibold leading-none text-xl md:text-2xl mb-6 md:mb-[30px]">
                        Información de pago
                    </h4>

                    <div class="grid gap-2 md:gap-2">
                        <div>
                            <label class="text-base md:text-lg text-title dark:text-white leading-none block">Nombre completo</label>
                            <input
                                v-model="form.billing.full_name"
                                class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                type="text"
                                placeholder="Ingrese su nombre completo"
                                required
                            >
                        </div>
                        <div>
                            <label class="text-base md:text-lg text-title dark:text-white leading-none block">Correo</label>
                            <input
                                v-model="form.billing.email"
                                class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                type="email"
                                placeholder="Ingrese su correo"
                                required
                            >
                        </div>
                        <div>
                            <label class="text-base md:text-lg text-title dark:text-white leading-none block">Teléfono</label>
                            <input
                                v-model="form.billing.phone"
                                class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                type="tel"
                                placeholder="Ingrese su número de teléfono"
                                required
                            >
                        </div>

                        <div class="grid md:grid-cols-2 gap-5 md:gap-6">
                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none block">Ciudad</label>
                                <input
                                    v-model="form.billing.city"
                                    class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                    type="text"
                                    placeholder="Ciudad"
                                >
                            </div>
                            <div>
                                <label class="text-base md:text-lg text-title dark:text-white leading-none block">Código Zip</label>
                                <input
                                    v-model="form.billing.zip"
                                    class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                    type="text"
                                    placeholder="1217"
                                >
                            </div>
                        </div>

                        <div>
                            <label class="text-base md:text-lg text-title dark:text-white leading-none block">Dirección</label>
                            <input
                                v-model="form.billing.address1"
                                class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                type="text"
                                placeholder="Dirección completa"
                                required
                            >
                        </div>
                        <div>
                            <label class="text-base md:text-lg text-title dark:text-white leading-none block">Dirección (línea 2)</label>
                            <input
                                v-model="form.billing.address2"
                                class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                type="text"
                                placeholder="Apartamento, local, referencia"
                            >
                        </div>
                        <div>
                            <label class="text-base md:text-lg text-title dark:text-white leading-none block">Nota de la compra</label>
                            <textarea
                                v-model="form.billing.note"
                                class="w-full h-[120px] bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                placeholder="Comentarios adicionales"
                            />
                        </div>
                    </div>
                </div>

                <div class="space-y-6">
                    <div class="bg-[#FAFAFA] dark:bg-dark-secondary pt-[30px] md:pt-[40px] lg:pt-[50px] px-[30px] md:px-[40px] lg:px-[50px] pb-[30px] border border-[#17243026] border-opacity-15 rounded-xl">
                        <h4 class="font-semibold leading-none text-xl md:text-2xl mb-6 md:mb-10">Información de producto</h4>

                        <div class="grid gap-5 md:gap-6">
                            <div v-for="item in snapshotItems" :key="`item-${item.key || `${item.type}-${item.id}`}`" class="flex items-center justify-between gap-5">
                                <div class="flex items-center gap-3 md:gap-4 lg:gap-6 cart-product flex-wrap">
                                    <div class="w-16 sm:w-[70px] flex-none">
                                        <img :src="item.image || '/img/placeholder.png'" :alt="item.name">
                                    </div>
                                    <div class="flex-1">
                                        <h6 class="leading-none font-medium text-sm text-gray-500">{{ capitalize(item.type) }}</h6>
                                        <h5 class="font-semibold leading-none mt-1 text-base md:text-lg">{{ item.name }}</h5>
                                        <ul v-if="item.options && Object.keys(item.options).length" class="mt-1 text-xs text-gray-500 flex flex-wrap gap-2">
                                            <li v-for="(value, key) in item.options" :key="`${item.id}-${key}`">
                                                <span class="font-medium">{{ capitalize(key) }}:</span> {{ value }}
                                            </li>
                                        </ul>
                                        <p class="mt-1 text-sm text-gray-600">Cantidad: {{ Number(item.qty || 0) }} × {{ formatMoney(item.price) }}</p>
                                    </div>
                                </div>
                                <h6 class="leading-none text-lg font-bold">{{ formatMoney((Number(item.price || 0) * Number(item.qty || 0))) }}</h6>
                            </div>
                        </div>

                        <div class="mt-6 pt-6 border-t border-bdr-clr dark:border-bdr-clr-drk text-right flex justify-end flex-col w-full ml-auto mr-0">
                            <div class="flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium">
                                <span>Sub Total:</span>
                                <span>{{ formatMoney(subtotal) }}</span>
                            </div>
                            <div class="mt-3 flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium">
                                <span>Impuesto:</span>
                                <span>{{ formatMoney(tax) }}</span>
                            </div>
                            <div class="mt-3 flex justify-between flex-wrap text-base sm:text-lg text-title dark:text-white font-medium">
                                <span>{{ selectedShippingLabel }}:</span>
                                <span>{{ formatMoney(selectedShippingAmount) }}</span>
                            </div>
                        </div>

                        

                        <div class="mt-6 pt-6 border-t border-bdr-clr dark:border-bdr-clr-drk">
                            <div class="flex justify-between flex-wrap font-semibold leading-none text-2xl md:text-3xl">
                                <span>Total:</span>
                                <span>{{ formatMoney(total) }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="border border-[#E3E5E6] rounded-lg p-4 md:p-6 bg-white dark:bg-dark-secondary">
                        <h5 class="font-semibold text-lg md:text-xl">Entrega</h5>
                        <p class="text-sm text-gray-600 mt-2">Escoge cómo quieres recibir la compra.</p>

                        <div class="mt-4 grid gap-3">
                            <label
                                v-for="option in shippingOptionsList"
                                :key="`shipping-${option.key}`"
                                class="payment-method-option categoryies-iteem flex items-start justify-between gap-3 border rounded-md px-4 py-3 cursor-pointer"
                                :class="form.shipping_method === option.key ? 'border-primary bg-[#FFF7ED]' : 'border-[#E3E5E6]'"
                            >
                                <span class="flex items-start gap-2">
                                    <input v-model="form.shipping_method" class="sr-only" type="radio" :value="option.key">
                                    <span class="mt-1 w-4 h-4 rounded-full border border-title dark:border-white flex items-center justify-center duration-300 flex-none">
                                        <svg class="duration-300" :class="form.shipping_method === option.key ? 'opacity-100' : 'opacity-0'" width="8" height="8" viewBox="0 0 10 10" fill="none">
                                            <rect width="10" height="10" rx="5" fill="#BB976D" />
                                        </svg>
                                    </span>
                                    <span>
                                        <span class="block text-sm md:text-base text-title dark:text-white">{{ option.label }}</span>
                                        <span v-if="option.description" class="block mt-1 text-xs md:text-sm text-gray-600">{{ option.description }}</span>
                                    </span>
                                </span>
                                <span class="text-xs md:text-sm text-gray-500 flex-none">{{ formatMoney(option.amount) }}</span>
                            </label>
                        </div>

                        <div v-if="isPickup" class="mt-4 rounded border border-blue-300 bg-blue-50 text-blue-800 px-4 py-3 text-sm">
                            La información de retiro quedará guardada en el recibo de pago. Podrás retirar desde el siguiente día hábil después de crear la orden; coordina el horario exacto con la tienda.
                        </div>
                    </div>

                    <div class="border border-[#E3E5E6] rounded-lg p-4 md:p-6 bg-white dark:bg-dark-secondary">
                        <h5 class="font-semibold text-lg md:text-xl">Método de compra</h5>
                        <p class="text-sm text-gray-600 mt-2">Puedes pagar con tarjeta (Wompi), PayPal o Yappy Panamá.</p>

                        <div class="mt-4 grid gap-3">
                            <label class="payment-method-option categoryies-iteem flex items-center justify-between gap-3 border rounded-md px-4 py-3 cursor-pointer" :class="form.payment_method === '1' ? 'border-primary bg-[#FFF7ED]' : 'border-[#E3E5E6]'">
                                <span class="flex items-center gap-2">
                                    <input v-model="form.payment_method" class="sr-only" type="radio" value="1">
                                    <span class="w-4 h-4 rounded-full border border-title dark:border-white flex items-center justify-center duration-300">
                                        <svg class="duration-300" :class="form.payment_method === '1' ? 'opacity-100' : 'opacity-0'" width="8" height="8" viewBox="0 0 10 10" fill="none">
                                            <rect width="10" height="10" rx="5" fill="#BB976D" />
                                        </svg>
                                    </span>
                                    <span class="text-sm md:text-base text-title dark:text-white">Tarjeta de crédito o débito (Wompi)</span>
                                </span>
                                <span class="text-xs text-gray-500">Recomendado</span>
                            </label>

                            <label class="payment-method-option categoryies-iteem flex items-center justify-between gap-3 border rounded-md px-4 py-3 cursor-pointer" :class="form.payment_method === '2' ? 'border-primary bg-[#FFF7ED]' : 'border-[#E3E5E6]'">
                                <span class="flex items-center gap-2">
                                    <input v-model="form.payment_method" class="sr-only" type="radio" value="2">
                                    <span class="w-4 h-4 rounded-full border border-title dark:border-white flex items-center justify-center duration-300">
                                        <svg class="duration-300" :class="form.payment_method === '2' ? 'opacity-100' : 'opacity-0'" width="8" height="8" viewBox="0 0 10 10" fill="none">
                                            <rect width="10" height="10" rx="5" fill="#BB976D" />
                                        </svg>
                                    </span>
                                    <span class="text-sm md:text-base text-title dark:text-white">PayPal</span>
                                </span>
                                <span class="text-xs text-gray-500">Checkout externo</span>
                            </label>

                            <label class="payment-method-option categoryies-iteem flex items-center justify-between gap-3 border rounded-md px-4 py-3 cursor-pointer" :class="form.payment_method === '3' ? 'border-primary bg-[#FFF7ED]' : 'border-[#E3E5E6]'">
                                <span class="flex items-center gap-2">
                                    <input v-model="form.payment_method" class="sr-only" type="radio" value="3">
                                    <span class="w-4 h-4 rounded-full border border-title dark:border-white flex items-center justify-center duration-300">
                                        <svg class="duration-300" :class="form.payment_method === '3' ? 'opacity-100' : 'opacity-0'" width="8" height="8" viewBox="0 0 10 10" fill="none">
                                            <rect width="10" height="10" rx="5" fill="#BB976D" />
                                        </svg>
                                    </span>
                                    <span class="text-sm md:text-base text-title dark:text-white">Yappy Panamá</span>
                                </span>
                                <span class="text-xs text-gray-500">Transferencia inmediata</span>
                            </label>
                        </div>

                        <div v-if="isWompi" class="mt-6">
                            <h6 class="font-semibold text-base md:text-lg">Pago con tarjeta de crédito (Wompi)</h6>
                            <p class="text-sm text-gray-600 mt-2">
                                Completa los datos del tarjetahabiente. Los datos sensibles de la tarjeta se ingresan de forma segura en Wompi.
                            </p>
                            <div v-if="!integration.wompi.ready" class="mt-4 rounded border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
                                Falta configurar llaves de integración de Wompi: {{ integration.wompi.missing.join(', ') }}
                            </div>
                            <p class="text-sm text-gray-600 mt-1">
                                Total a pagar:
                                <span class="font-semibold">USD {{ formatMoney(total) }}</span>
                            </p>

                            <div class="grid md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label class="text-base text-title dark:text-white leading-none block">Tipo de documento</label>
                                    <select
                                        v-model="form.wompi.legal_id_type"
                                        class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary px-4 outline-none duration-300"
                                    >
                                        <option value="">Selecciona</option>
                                        <option value="CC">Cédula</option>
                                        <option value="CE">Carné de extranjería</option>
                                        <option value="RUC">RUC</option>
                                        <option value="PP">Pasaporte</option>
                                    </select>
                                </div>
                                <div>
                                    <label class="text-base text-title dark:text-white leading-none block">Número de documento</label>
                                    <input
                                        v-model="form.wompi.legal_id"
                                        class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                        type="text"
                                        placeholder="Ej: 8-123-456"
                                    >
                                </div>
                            </div>

                            <div class="mt-4">
                                <label class="flex items-center gap-2 iam-agree">
                                    <input v-model="form.terms" class="sr-only" type="checkbox">
                                    <span class="w-6 h-6 rounded-[5px] border-2 border-title dark:border-white flex items-center justify-center duration-300">
                                        <svg class="duration-300 text-title dark:text-white fill-current" :class="form.terms ? 'opacity-100' : 'opacity-0'" width="15" height="12" viewBox="0 0 20 15" fill="none">
                                            <path d="M18.3819 0.742676L6.10461 11.8998L2.25731 8.06381L0.763672 9.55745L6.20645 15.0002L20 2.32686L18.3819 0.742676Z" />
                                        </svg>
                                    </span>
                                    <span class="text-sm text-title dark:text-white leading-none sm:leading-none select-none inline-block transform translate-y-[3px]">
                                        Confirmo que soy el titular o autorizado de la tarjeta.
                                    </span>
                                </label>
                            </div>

                            <div v-if="wompiState.error" class="mt-4 rounded border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
                                {{ wompiState.error }}
                            </div>
                            <div v-if="wompiState.message" class="mt-4 rounded border px-4 py-3 text-sm" :class="statusCssClass(wompiState.severity)">
                                {{ wompiState.message }}
                            </div>
                            <div ref="wompiWidgetContainer" class="mt-4"></div>
                        </div>

                        <div v-if="isPayPal" class="mt-6">
                            <h6 class="font-semibold text-base md:text-lg">Pago con PayPal</h6>
                            <p class="text-sm text-gray-600 mt-2">Serás redirigido al checkout seguro de PayPal para completar el pago.</p>
                            <div v-if="!integration.paypal.ready" class="mt-4 rounded border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
                                Falta configurar llaves de integración de PayPal: {{ integration.paypal.missing.join(', ') }}
                            </div>
                            <div class="grid md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label class="text-base text-title dark:text-white leading-none block">Correo PayPal</label>
                                    <input
                                        v-model="form.paypal.payer_email"
                                        class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                        type="email"
                                        placeholder="correo@paypal.com"
                                    >
                                </div>
                            </div>
                            <div class="mt-4">
                                <label class="flex items-center gap-2 iam-agree">
                                    <input v-model="form.paypal.confirmed" class="sr-only" type="checkbox">
                                    <span class="w-6 h-6 rounded-[5px] border-2 border-title dark:border-white flex items-center justify-center duration-300">
                                        <svg class="duration-300 text-title dark:text-white fill-current" :class="form.paypal.confirmed ? 'opacity-100' : 'opacity-0'" width="15" height="12" viewBox="0 0 20 15" fill="none">
                                            <path d="M18.3819 0.742676L6.10461 11.8998L2.25731 8.06381L0.763672 9.55745L6.20645 15.0002L20 2.32686L18.3819 0.742676Z" />
                                        </svg>
                                    </span>
                                    <span class="text-sm text-title dark:text-white leading-none sm:leading-none select-none inline-block transform translate-y-[3px]">
                                        Confirmo que continuaré al checkout de PayPal para completar el pago.
                                    </span>
                                </label>
                            </div>
                        </div>

                        <div v-if="isYappy" class="mt-6">
                            <h6 class="font-semibold text-base md:text-lg">Pago con Yappy Panamá</h6>
                            <p class="text-sm text-gray-600 mt-2">{{ integration.yappy.instructions }}</p>
                            <div v-if="!integration.yappy.ready" class="mt-4 rounded border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
                                Falta configurar llaves de integración de Yappy: {{ integration.yappy.missing.join(', ') }}
                            </div>
                            <div class="mt-4 rounded border border-blue-300 bg-blue-50 text-blue-800 px-4 py-3 text-sm">
                                <div><span class="font-semibold">Comercio:</span> {{ integration.yappy.recipient_name }}</div>
                                <div class="mt-1"><span class="font-semibold">Dominio registrado:</span> {{ integration.yappy.domain || 'No configurado' }}</div>
                                <div v-if="integration.yappy.destination_phone" class="mt-1"><span class="font-semibold">Contacto:</span> {{ integration.yappy.destination_phone }}</div>
                            </div>
                            <div v-if="yappyState.online === false" class="mt-3 rounded border border-yellow-300 bg-yellow-50 text-yellow-800 px-4 py-3 text-sm">
                                El botón oficial reporta indisponibilidad temporal. Intenta nuevamente en unos minutos.
                            </div>

                            <div class="grid md:grid-cols-2 gap-4 mt-4">
                                <div>
                                    <label class="text-base text-title dark:text-white leading-none block">Tu número de Yappy</label>
                                    <input
                                        v-model="form.yappy.payer_phone"
                                        class="w-full h-12 md:h-14 bg-white dark:bg-dark-secondary border border-[#E3E5E6] text-title dark:text-white focus:border-primary p-4 outline-none duration-300"
                                        type="text"
                                        placeholder="Ej: 6123-4567"
                                    >
                                </div>
                                <div>
                                    <label class="text-base text-title dark:text-white leading-none block">Total a cobrar</label>
                                    <div class="w-full h-12 md:h-14 bg-[#F8FAFC] border border-[#E3E5E6] text-title dark:text-white px-4 flex items-center font-semibold">
                                        USD {{ formatMoney(total) }}
                                    </div>
                                </div>
                            </div>
                            <div v-if="yappyState.error" class="mt-4 rounded border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm">
                                {{ yappyState.error }}
                            </div>
                            <div v-if="yappyState.message" class="mt-4 rounded border px-4 py-3 text-sm" :class="statusCssClass(yappyState.severity)">
                                {{ yappyState.message }}
                            </div>
                            <div ref="yappyButtonContainer" class="mt-4"></div>
                            <p class="mt-3 text-xs text-gray-500">
                                El botón oficial abrirá el flujo seguro de Yappy y la orden se actualizará automáticamente cuando recibamos la confirmación.
                            </p>
                        </div>
                    </div>

                    <div class="flex flex-wrap gap-3">
                        <a :href="routes.cart" class="btn btn-outline">
                            <span>Regresar el carrito</span>
                        </a>
                        <button
                            type="button"
                            class="btn btn-theme-solid checkout-action-btn"
                            :disabled="isSubmitting || yappyState.loading || (isWompi && !integration.wompi.ready) || (isPayPal && !integration.paypal.ready) || (isYappy && !integration.yappy.ready)"
                            @click="submitCheckout"
                        >
                            <span>{{ submitButtonLabel }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'CheckoutPage',
    props: {
        initialData: {
            type: Object,
            required: true,
        },
    },
    data() {
        const defaults = this.initialData?.defaults || {};
        const billing = defaults.billing || {};

        return {
            routes: this.initialData?.routes || {},
            csrfToken: this.initialData?.csrfToken || '',
            snapshot: this.initialData?.snapshot || {},
            shippingOptions: this.initialData?.shippingOptions || {},
            integration: this.initialData?.integration || {
                wompi: { ready: false, missing: [] },
                paypal: { ready: false, missing: [] },
                yappy: { ready: false, missing: [], initEndpoint: '', statusEndpoint: '', scriptUrl: '', domain: '', recipient_name: '', destination_phone: '', instructions: '' },
            },
            pendingOrders: this.initialData?.pendingOrders || [],
            showPendingOrdersBox: true,
            validationErrors: [...(this.initialData?.alerts?.errors || [])],
            flashMessage: {
                success: this.initialData?.alerts?.success || '',
                warning: this.initialData?.alerts?.warning || '',
                error: this.initialData?.alerts?.error || '',
            },
            form: {
                coupon: defaults.coupon || '',
                shipping_method: String(defaults.shippingMethod || ''),
                payment_method: String(defaults.paymentMethod || '1'),
                billing: {
                    full_name: billing.full_name || '',
                    email: billing.email || '',
                    phone: billing.phone || '',
                    city: billing.city || '',
                    zip: billing.zip || '',
                    address1: billing.address1 || '',
                    address2: billing.address2 || '',
                    note: billing.note || '',
                },
                terms: !!defaults.terms,
                wompi: {
                    legal_id_type: defaults.wompiLegalIdType || '',
                    legal_id: defaults.wompiLegalId || '',
                },
                paypal: {
                    payer_email: defaults.paypalPayerEmail || '',
                    confirmed: !!defaults.paypalConfirmed,
                },
                yappy: {
                    payer_phone: defaults.yappyPayerPhone || '',
                },
            },
            isSubmitting: false,
            wompiState: {
                loading: false,
                error: '',
                message: '',
                severity: 'info',
            },
            wompiOrderCache: null,
            wompiOrderFingerprint: null,
            wompiPollingTimer: null,
            wompiLatestQuery: null,
            wompiThankYouUrl: '',
            yappyState: {
                loading: false,
                error: '',
                message: '',
                severity: 'info',
                online: null,
            },
            yappyOrderCache: null,
            yappyOrderFingerprint: null,
            yappyPollingTimer: null,
            yappyLatestQuery: null,
            yappyThankYouUrl: '',
            yappyScriptPromise: null,
        };
    },
    watch: {
        'form.payment_method'(value) {
            if (value === '3') {
                this.$nextTick(() => this.renderYappyButton());
                return;
            }

            this.stopYappyPolling();
        },
    },
    computed: {
        snapshotItems() {
            return this.snapshot?.items || [];
        },
        subtotal() {
            return Number(this.snapshot?.totals?.subtotal || 0);
        },
        tax() {
            return Number(this.snapshot?.totals?.tax || 0);
        },
        selectedShippingAmount() {
            const option = this.shippingOptions[this.form.shipping_method];
            return Number(option?.amount || 0);
        },
        selectedShippingLabel() {
            const option = this.shippingOptions[this.form.shipping_method];
            return option?.label || 'Envío';
        },
        shippingOptionsList() {
            return Object.entries(this.shippingOptions).map(([key, option]) => ({
                key,
                label: option?.label || key,
                description: option?.description || '',
                amount: Number(option?.amount || 0),
            }));
        },
        total() {
            return this.subtotal + this.tax + this.selectedShippingAmount;
        },
        isPickup() {
            return this.form.shipping_method === 'pickup';
        },
        isWompi() {
            return this.form.payment_method === '1';
        },
        isPayPal() {
            return this.form.payment_method === '2';
        },
        isYappy() {
            return this.form.payment_method === '3';
        },
        latestPendingOrder() {
            return this.pendingOrders.length ? this.pendingOrders[0] : null;
        },
        submitButtonLabel() {
            if (this.isSubmitting || this.wompiState.loading || this.yappyState.loading) {
                return 'Procesando...';
            }
            if (this.isWompi) {
                return 'Pagar con tarjeta';
            }
            if (this.isPayPal) {
                return 'Pagar con PayPal';
            }
            return 'Continuar con Yappy';
        },
    },
    mounted() {
        this.handleResumePrompt();
        this.resumeWompiStatusIfNeeded();
        if (this.isYappy) {
            this.$nextTick(() => this.renderYappyButton());
        }
    },
    beforeUnmount() {
        this.stopWompiPolling();
        this.stopYappyPolling();
    },
    methods: {
        mapOrderStatus(value) {
            const labels = {
                pending: 'Pendiente',
                awaiting_payment: 'En curso',
                paid: 'Pagada',
                failed: 'Fallida',
                cancelled: 'Cancelada',
            };

            const key = String(value || '').toLowerCase();
            return labels[key] || String(value || '').toUpperCase();
        },
        mapPaymentStatus(value) {
            const labels = {
                pending: 'Pendiente',
                paid: 'Pagado',
                failed: 'Fallido',
                cancelled: 'Cancelado',
            };

            const key = this.normalizePaymentStatus(value);
            return labels[key] || String(value || '').toUpperCase();
        },
        normalizePaymentStatus(value) {
            const normalized = String(value || '').trim().toLowerCase().replace(/[-\s]/g, '_');

            if (['paid', 'approved', 'completed', 'success', 'succeeded', 'captured', '1', 'true'].includes(normalized)) {
                return 'paid';
            }
            if (['failed', 'declined', 'denied', 'error', 'rejected', 'expired'].includes(normalized)) {
                return 'failed';
            }
            if (['cancelled', 'canceled', 'voided', 'reversed'].includes(normalized)) {
                return 'cancelled';
            }
            return 'pending';
        },
        statusCssClass(severity) {
            const map = {
                success: 'border-green-300 bg-green-50 text-green-700',
                warning: 'border-yellow-300 bg-yellow-50 text-yellow-800',
                danger: 'border-red-300 bg-red-50 text-red-700',
                info: 'border-blue-300 bg-blue-50 text-blue-700',
            };

            return map[severity] || map.info;
        },
        formatMoney(value) {
            return Number(value || 0).toFixed(2);
        },
        formatDateTime(value) {
            if (!value) return 'N/A';
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) return value;

            const pad = (num) => String(num).padStart(2, '0');
            return `${parsed.getFullYear()}-${pad(parsed.getMonth() + 1)}-${pad(parsed.getDate())} ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}`;
        },
        capitalize(value) {
            if (!value) return '';
            const text = String(value);
            return text.charAt(0).toUpperCase() + text.slice(1);
        },
        dismissPendingOrders() {
            this.showPendingOrdersBox = false;
            sessionStorage.setItem('checkout_resume_prompt_shown_v3', '1');
        },
        async handleResumePrompt() {
            if (!this.pendingOrders.length || !this.latestPendingOrder?.resume_url) {
                return;
            }
            if (sessionStorage.getItem('checkout_resume_prompt_shown_v3') === '1') {
                return;
            }

            let resumeOrder = false;
            if (window.Swal && typeof window.Swal.fire === 'function') {
                const result = await window.Swal.fire({
                    title: 'Pedido en curso',
                    text: 'Tienes un pedido pendiente o en curso. ¿Quieres retomarlo ahora?',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Sí, retomar',
                    cancelButtonText: 'No, continuar compra nueva',
                    reverseButtons: true,
                });
                resumeOrder = !!result.isConfirmed;
            } else {
                resumeOrder = window.confirm('Tienes un pedido pendiente o en curso. ¿Quieres retomarlo ahora?');
            }

            sessionStorage.setItem('checkout_resume_prompt_shown_v3', '1');
            if (resumeOrder) {
                window.location.href = this.latestPendingOrder.resume_url;
            }
        },
        clearTransientMessages() {
            this.validationErrors = [];
            this.flashMessage.success = '';
            this.flashMessage.warning = '';
            this.flashMessage.error = '';
            this.wompiState.error = '';
            this.wompiState.message = '';
            this.yappyState.error = '';
            this.yappyState.message = '';
        },
        validateCommonFields() {
            const errors = [];
            if (!this.form.billing.full_name.trim()) errors.push('Debes completar el nombre completo.');
            if (!this.form.billing.email.trim()) errors.push('Debes completar el correo.');
            if (!this.form.billing.phone.trim()) errors.push('Debes completar el teléfono.');
            if (!this.form.billing.address1.trim()) errors.push('Debes completar la dirección.');
            if (!this.form.shipping_method) errors.push('Selecciona un método de envío.');

            return errors;
        },
        validateByMethod() {
            const errors = [];
            if (this.isWompi) {
                if (!this.integration.wompi.ready) {
                    errors.push('La integración de Wompi no está disponible.');
                }
                if (!this.form.wompi.legal_id_type) {
                    errors.push('Selecciona el tipo de documento del tarjetahabiente.');
                }
                if (!this.form.wompi.legal_id.trim()) {
                    errors.push('Ingresa el número de documento del tarjetahabiente.');
                }
                if (!this.form.terms) {
                    errors.push('Debes confirmar autorización del titular para continuar.');
                }
            }

            if (this.isPayPal) {
                if (!this.integration.paypal.ready) {
                    errors.push('La integración de PayPal no está disponible.');
                }
                if (!this.form.paypal.payer_email.trim()) {
                    errors.push('Debes completar el correo de PayPal.');
                }
                if (!this.form.paypal.confirmed) {
                    errors.push('Debes confirmar que continuarás al checkout de PayPal.');
                }
            }

            if (this.isYappy) {
                if (!this.integration.yappy.ready) {
                    errors.push('La integración de Yappy no está disponible.');
                }
                if (!this.form.yappy.payer_phone.trim()) {
                    errors.push('Debes ingresar tu número de Yappy.');
                }
            }

            return errors;
        },
        buildOrderFormData(paymentMethodOverride = null) {
            const method = String(paymentMethodOverride || this.form.payment_method);
            const formData = new FormData();

            formData.set('billing[full_name]', this.form.billing.full_name.trim());
            formData.set('billing[email]', this.form.billing.email.trim());
            formData.set('billing[phone]', this.form.billing.phone.trim());
            formData.set('billing[city]', this.form.billing.city.trim());
            formData.set('billing[zip]', this.form.billing.zip.trim());
            formData.set('billing[address1]', this.form.billing.address1.trim());
            formData.set('billing[address2]', this.form.billing.address2.trim());
            formData.set('billing[note]', this.form.billing.note.trim());
            formData.set('coupon', this.form.coupon.trim());
            formData.set('shipping_method', this.form.shipping_method);
            formData.set('payment_method', method);

            if (method === '1') {
                formData.set('wompi[legal_id_type]', this.form.wompi.legal_id_type);
                formData.set('wompi[legal_id]', this.form.wompi.legal_id.trim());
                if (this.form.terms) {
                    formData.set('terms', '1');
                }
            }

            if (method === '2') {
                formData.set('paypal[payer_email]', this.form.paypal.payer_email.trim());
                if (this.form.paypal.confirmed) {
                    formData.set('paypal[confirmed]', '1');
                }
            }

            if (method === '3') {
                formData.set('yappy[payer_phone]', this.form.yappy.payer_phone.trim());
            }

            return formData;
        },
        async requestOrder(paymentMethodOverride = null) {
            const formData = this.buildOrderFormData(paymentMethodOverride);
            const response = await fetch(this.routes.pay, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'X-Requested-With': 'XMLHttpRequest',
                    'X-CSRF-TOKEN': this.csrfToken,
                },
                credentials: 'same-origin',
                body: formData,
            });

            let payload = {};
            try {
                payload = await response.json();
            } catch (error) {
                payload = {};
            }

            if (!response.ok) {
                if (response.status === 422 && payload.errors) {
                    const messages = [];
                    Object.values(payload.errors).forEach((errorList) => {
                        if (Array.isArray(errorList)) {
                            errorList.forEach((entry) => messages.push(entry));
                        }
                    });
                    throw new Error(messages[0] || 'Revisa los datos del formulario.');
                }

                throw new Error(payload?.message || payload?.error || 'No se pudo registrar la orden.');
            }

            return payload;
        },
        buildCheckoutFingerprint() {
            return [
                this.form.payment_method,
                this.form.billing.full_name,
                this.form.billing.email,
                this.form.billing.phone,
                this.form.billing.city,
                this.form.billing.address1,
                this.form.wompi.legal_id_type,
                this.form.wompi.legal_id,
                this.form.paypal.payer_email,
                this.form.yappy.payer_phone,
                this.form.shipping_method,
                this.total,
            ].join('|');
        },
        async ensureYappyScriptLoaded() {
            if (window.customElements && window.customElements.get('btn-yappy')) {
                return;
            }

            if (this.yappyScriptPromise) {
                return this.yappyScriptPromise;
            }

            this.yappyScriptPromise = new Promise((resolve, reject) => {
                const existing = document.querySelector('script[data-yappy-sdk="1"]');
                if (existing) {
                    if (existing.dataset.loaded === '1') {
                        resolve();
                        return;
                    }

                    existing.addEventListener('load', () => {
                        existing.dataset.loaded = '1';
                        resolve();
                    }, { once: true });
                    existing.addEventListener('error', () => reject(new Error('No se pudo cargar el SDK de Yappy.')), { once: true });
                    return;
                }

                const script = document.createElement('script');
                script.type = 'module';
                script.async = true;
                script.src = this.integration.yappy.scriptUrl;
                script.dataset.yappySdk = '1';
                script.addEventListener('load', () => {
                    script.dataset.loaded = '1';
                    resolve();
                }, { once: true });
                script.addEventListener('error', () => reject(new Error('No se pudo cargar el SDK de Yappy.')), { once: true });
                document.head.appendChild(script);
            });

            return this.yappyScriptPromise;
        },
        async renderYappyButton() {
            if (!this.isYappy || !this.integration.yappy.ready) {
                return;
            }

            const container = this.$refs.yappyButtonContainer;
            if (!container) {
                return;
            }

            if (container.querySelector('btn-yappy')) {
                return;
            }

            await this.ensureYappyScriptLoaded();

            const button = document.createElement('btn-yappy');
            button.classList.add('w-full', 'block');
            button.addEventListener('eventClick', this.handleYappyEventClick);
            button.addEventListener('eventSuccess', this.handleYappySuccess);
            button.addEventListener('eventCancel', this.handleYappyCancel);
            button.addEventListener('eventError', this.handleYappyError);
            button.addEventListener('isYappyOnline', this.handleYappyOnlineState);

            container.innerHTML = '';
            container.appendChild(button);
        },
        async triggerYappyButton() {
            await this.renderYappyButton();

            const button = this.$refs.yappyButtonContainer?.querySelector('btn-yappy');
            if (!button || typeof button.click !== 'function') {
                throw new Error('No se pudo cargar el botón oficial de Yappy.');
            }

            button.click();
        },
        async handleYappyEventClick(event) {
            this.clearTransientMessages();
            this.yappyState.loading = true;
            this.yappyState.error = '';
            this.yappyState.message = 'Preparando tu orden para Yappy...';
            this.yappyState.severity = 'info';
            let paymentDispatched = false;

            const button = event?.currentTarget;
            if (button) {
                button.isButtonLoading = true;
            }

            try {
                const commonErrors = this.validateCommonFields();
                const methodErrors = this.validateByMethod();
                const allErrors = [...commonErrors, ...methodErrors];
                if (allErrors.length > 0) {
                    this.validationErrors = allErrors;
                    return;
                }

                const fingerprint = this.buildCheckoutFingerprint();
                if (!this.yappyOrderCache || this.yappyOrderFingerprint !== fingerprint) {
                    const orderPayload = await this.requestOrder('3');
                    this.yappyOrderCache = orderPayload;
                    this.yappyOrderFingerprint = fingerprint;
                    this.yappyThankYouUrl = orderPayload.thank_you_url || '';
                }

                const initResponse = await fetch(this.integration.yappy.initEndpoint, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-CSRF-TOKEN': this.csrfToken,
                    },
                    credentials: 'same-origin',
                    body: JSON.stringify({
                        order_id: this.yappyOrderCache?.order?.id,
                    }),
                });

                const data = await initResponse.json();
                if (!initResponse.ok) {
                    throw new Error(data?.error?.reason || 'No se pudo iniciar el pago con Yappy.');
                }

                if (!button || typeof button.eventPayment !== 'function') {
                    throw new Error('No se pudo abrir el checkout oficial de Yappy.');
                }

                this.yappyLatestQuery = {
                    merchant_order_id: data.merchant_order_id,
                    transaction_id: data.transaction_id,
                    order_id: this.yappyOrderCache?.order?.id,
                };

                button.eventPayment({
                    token: data.token,
                    transactionId: data.transaction_id,
                    documentName: data.document_name,
                });

                paymentDispatched = true;
                this.yappyState.message = 'Abriendo checkout seguro de Yappy...';
            } catch (error) {
                this.yappyState.error = error?.message || 'No fue posible iniciar el pago con Yappy.';
                this.yappyState.message = '';
                this.yappyState.severity = 'danger';
            } finally {
                if (button) {
                    button.isButtonLoading = false;
                }
                if (!paymentDispatched) {
                    this.yappyState.loading = false;
                }
            }
        },
        handleYappySuccess() {
            this.yappyState.loading = false;
            this.yappyState.error = '';
            this.yappyState.message = 'Yappy reportó el cobro. Estamos confirmando la transacción...';
            this.yappyState.severity = 'info';

            this.stopYappyPolling();
            if (this.yappyLatestQuery) {
                this.yappyPollingTimer = setInterval(() => this.pollYappyStatus(this.yappyLatestQuery), 3500);
                this.pollYappyStatus(this.yappyLatestQuery);
            }
        },
        handleYappyCancel() {
            this.yappyState.loading = false;
            this.yappyState.error = '';
            this.yappyState.message = 'Cancelaste el flujo de Yappy.';
            this.yappyState.severity = 'warning';
            this.stopYappyPolling();
        },
        handleYappyError(event) {
            this.yappyState.loading = false;
            this.yappyState.error = event?.detail?.message || 'Yappy no pudo completar la operación.';
            this.yappyState.message = '';
            this.yappyState.severity = 'danger';
            this.stopYappyPolling();
        },
        handleYappyOnlineState(event) {
            if (typeof event?.detail === 'boolean') {
                this.yappyState.online = event.detail;
                return;
            }

            if (event?.detail && typeof event.detail.online === 'boolean') {
                this.yappyState.online = event.detail.online;
            }
        },
        stopYappyPolling() {
            if (!this.yappyPollingTimer) return;
            clearInterval(this.yappyPollingTimer);
            this.yappyPollingTimer = null;
        },
        async pollYappyStatus(query) {
            const params = new URLSearchParams();
            if (query?.merchant_order_id) params.set('merchant_order_id', query.merchant_order_id);
            if (query?.transaction_id) params.set('transaction_id', query.transaction_id);
            if (query?.order_id) params.set('order_id', query.order_id);
            if (!params.toString()) return;

            try {
                const response = await fetch(`${this.integration.yappy.statusEndpoint}?${params.toString()}`, {
                    headers: {
                        Accept: 'application/json',
                    },
                    credentials: 'same-origin',
                });

                const data = await response.json();
                if (!response.ok) {
                    throw new Error(data?.error?.reason || 'No pudimos verificar el estado del pago Yappy.');
                }

                if (data.payment_status === 'paid') {
                    this.yappyState.message = data.ui_message || 'Pago Yappy aprobado correctamente.';
                    this.yappyState.severity = 'success';
                    this.stopYappyPolling();

                    if (this.yappyThankYouUrl) {
                        window.location.href = this.yappyThankYouUrl;
                    }
                    return;
                }

                if (['failed', 'cancelled'].includes(data.payment_status)) {
                    this.yappyState.message = data.ui_message || 'El pago Yappy no se completó.';
                    this.yappyState.severity = data.payment_status === 'cancelled' ? 'warning' : 'danger';
                    this.stopYappyPolling();
                    return;
                }

                this.yappyState.message = data.ui_message || 'Estamos esperando la confirmación de Yappy...';
                this.yappyState.severity = 'info';
            } catch (error) {
                this.yappyState.message = error?.message || 'No pudimos verificar el estado del pago Yappy.';
                this.yappyState.severity = 'warning';
                this.stopYappyPolling();
            }
        },
        normalizePhone(phone) {
            return String(phone || '').replace(/[^\d]/g, '');
        },
        renderWompiWidget(config) {
            const container = this.$refs.wompiWidgetContainer;
            if (!container) return;

            container.innerHTML = '';
            const form = document.createElement('form');
            const script = document.createElement('script');
            script.src = this.integration.wompi.widgetUrl;
            script.async = true;
            script.setAttribute('data-render', 'button');
            script.setAttribute('data-public-key', config.public_key);
            script.setAttribute('data-currency', config.currency || 'USD');
            script.setAttribute('data-amount-in-cents', String(config.amount_in_cents));
            script.setAttribute('data-reference', config.reference);
            script.setAttribute('data-signature:integrity', config.signature_integrity);
            script.setAttribute('data-redirect-url', config.redirect_url || this.routes.checkout);
            script.setAttribute('data-default-language', 'es');
            script.setAttribute('data-customer-data:email', this.form.billing.email.trim());
            script.setAttribute('data-customer-data:full-name', this.form.billing.full_name.trim());
            script.setAttribute('data-customer-data:phone-number', this.normalizePhone(this.form.billing.phone));
            script.setAttribute('data-customer-data:phone-number-prefix', '+507');
            script.setAttribute('data-customer-data:legal-id', this.form.wompi.legal_id.trim());
            script.setAttribute('data-customer-data:legal-id-type', this.form.wompi.legal_id_type);
            script.setAttribute('data-shipping-address:address-line-1', this.form.billing.address1.trim());
            script.setAttribute('data-shipping-address:country', 'PA');
            script.setAttribute('data-shipping-address:city', this.form.billing.city.trim());
            script.setAttribute('data-shipping-address:region', this.form.billing.city.trim());
            script.setAttribute('data-shipping-address:name', this.form.billing.full_name.trim());
            script.setAttribute('data-shipping-address:phone-number', this.normalizePhone(this.form.billing.phone));

            form.appendChild(script);
            container.appendChild(form);

            setTimeout(() => {
                const button = container.querySelector('button');
                if (button) {
                    button.click();
                }
            }, 650);
        },
        stopWompiPolling() {
            if (!this.wompiPollingTimer) return;
            clearInterval(this.wompiPollingTimer);
            this.wompiPollingTimer = null;
        },
        async pollWompiStatus(query) {
            const params = new URLSearchParams();
            if (query?.id) params.set('id', query.id);
            if (query?.reference) params.set('reference', query.reference);
            if (!params.toString()) return;

            try {
                const response = await fetch(`${this.integration.wompi.statusEndpoint}?${params.toString()}`, {
                    headers: { Accept: 'application/json' },
                    credentials: 'same-origin',
                });
                const data = await response.json();
                const paymentStatus = this.normalizePaymentStatus(data.payment_status || data.status);

                if (paymentStatus === 'paid') {
                    this.wompiState.message = data.ui_message || 'Pago aprobado correctamente.';
                    this.wompiState.severity = 'success';
                    this.stopWompiPolling();

                    if (this.wompiThankYouUrl) {
                        window.location.href = this.wompiThankYouUrl;
                    }
                    return;
                }

                if (['failed', 'cancelled'].includes(paymentStatus)) {
                    const reason = data.error_reason ? ` Motivo: ${data.error_reason}` : '';
                    this.wompiState.message = `El pago no se completó.${reason}`;
                    this.wompiState.severity = 'danger';
                    this.stopWompiPolling();
                    return;
                }

                this.wompiState.message = data.ui_message || 'Estamos confirmando tu pago...';
                this.wompiState.severity = 'info';
            } catch (error) {
                this.wompiState.message = 'No pudimos verificar el estado del pago. Intenta de nuevo.';
                this.wompiState.severity = 'warning';
                this.stopWompiPolling();
            }
        },
        resumeWompiStatusIfNeeded() {
            const params = new URLSearchParams(window.location.search);
            const transactionId = params.get('id');
            const reference = params.get('reference');
            if (!transactionId && !reference) return;

            this.form.payment_method = '1';
            this.wompiLatestQuery = transactionId ? { id: transactionId } : { reference };
            this.wompiState.message = 'Validando estado de tu pago...';
            this.wompiState.severity = 'info';

            this.stopWompiPolling();
            this.wompiPollingTimer = setInterval(() => this.pollWompiStatus(this.wompiLatestQuery), 5000);
            this.pollWompiStatus(this.wompiLatestQuery);
        },
        async submitWompi() {
            this.wompiState.error = '';
            this.wompiState.message = '';
            this.wompiState.severity = 'info';

            const fingerprint = this.buildCheckoutFingerprint();
            if (!this.wompiOrderCache || this.wompiOrderFingerprint !== fingerprint) {
                const orderPayload = await this.requestOrder('1');
                this.wompiOrderCache = orderPayload;
                this.wompiOrderFingerprint = fingerprint;
                this.wompiThankYouUrl = orderPayload.thank_you_url || '';
            }

            const initResponse = await fetch(this.integration.wompi.initEndpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    'X-CSRF-TOKEN': this.csrfToken,
                },
                credentials: 'same-origin',
                body: JSON.stringify({
                    order_id: this.wompiOrderCache?.order?.id,
                    amount_in_cents: this.wompiOrderCache?.amount_in_cents || Math.round(this.total * 100),
                    customer_email: this.form.billing.email.trim(),
                    redirect_url: this.wompiThankYouUrl || this.routes.checkout,
                }),
            });

            const data = await initResponse.json();
            if (!initResponse.ok) {
                throw new Error(data?.error?.reason || 'No se pudo iniciar el pago con Wompi.');
            }

            this.renderWompiWidget(data);
            this.wompiState.message = 'Abriendo pasarela segura de Wompi...';
            this.wompiState.severity = 'info';

            this.wompiLatestQuery = { reference: data.reference };
            this.stopWompiPolling();
            this.wompiPollingTimer = setInterval(() => this.pollWompiStatus(this.wompiLatestQuery), 5000);
            this.pollWompiStatus(this.wompiLatestQuery);
        },
        async submitPayPal() {
            const payload = await this.requestOrder('2');
            const approvalUrl = payload?.approval_url || '';
            if (!approvalUrl) {
                throw new Error('No recibimos URL de aprobación de PayPal.');
            }

            window.location.href = approvalUrl;
        },
        async submitCheckout() {
            this.clearTransientMessages();
            this.isSubmitting = true;
            this.wompiState.loading = this.isWompi;

            try {
                const commonErrors = this.validateCommonFields();
                const methodErrors = this.validateByMethod();
                const allErrors = [...commonErrors, ...methodErrors];
                if (allErrors.length > 0) {
                    this.validationErrors = allErrors;
                    return;
                }

                if (this.isWompi) {
                    await this.submitWompi();
                    return;
                }
                if (this.isPayPal) {
                    await this.submitPayPal();
                    return;
                }
                await this.triggerYappyButton();
            } catch (error) {
                const message = error?.message || 'No fue posible completar el checkout.';
                if (this.isWompi) {
                    this.wompiState.error = message;
                    this.wompiState.severity = 'danger';
                    this.wompiState.message = 'No fue posible iniciar el pago.';
                } else if (this.isYappy) {
                    this.yappyState.error = message;
                    this.yappyState.severity = 'danger';
                    this.yappyState.message = '';
                } else {
                    this.validationErrors = [message];
                }
            } finally {
                this.isSubmitting = false;
                this.wompiState.loading = false;
            }
        },
    },
};
</script>
